import { useState, useEffect } from "react";
import { Button, Checkbox, InputNumber, Popover } from "antd";
import { useTranslation } from "react-i18next";
import { MdDragIndicator, MdSettings, MdRestore } from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

// Sub-component for each sortable column item
const SortableItem = ({ id, col, onVisibilityChange, onWidthChange, t }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 mb-1 rounded border transition-colors ${
        isDragging 
          ? "bg-blue-50 border-blue-300 shadow-md" 
          : "bg-gray-50 dark:bg-gray-800 border-transparent hover:border-blue-200"
      }`}
    >
      <div {...attributes} {...listeners} className="text-gray-400 cursor-grab active:cursor-grabbing p-1">
        <MdDragIndicator size={20} />
      </div>
      
      <Checkbox 
        checked={col.visible} 
        onChange={(e) => onVisibilityChange(id, e.target.checked)} 
      />
      
      <span className="flex-1 truncate text-sm text-gray-700 dark:text-gray-300" title={col.title}>
        {col.title}
      </span>
      
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-gray-400">px</span>
        <InputNumber
          size="small"
          min={50}
          max={600}
          value={col.width}
          placeholder={t("common.auto")}
          onChange={(val) => onWidthChange(id, val)}
          className="w-[65px]"
        />
      </div>
    </div>
  );
};

const ColVisibilityDropdown = ({ options, columns, columnsToShowHandler, onReset }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  
  // Helper to get a stable ID for a column (must be non-numeric string for dnd-kit sometimes, but string is safer)
  const getColId = (col) => col.key || col.dataIndex || col.title;

  const [manageableCols, setManageableCols] = useState([]);
  const [fixedStart, setFixedStart] = useState([]);
  const [fixedEnd, setFixedEnd] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (open) {
      const mapped = options.map(opt => {
        const optId = getColId(opt);
        const existingInView = columns.find(c => getColId(c) === optId);
        return {
          ...opt,
          // read the saved visible flag; fall back to true if column not yet in state
          visible: existingInView ? (existingInView.visible !== false) : true,
          width: existingInView?.width || opt.width
        };
      });
      
      const ordered = [];
      columns.forEach(c => {
          const cId = getColId(c);
          const found = mapped.find(l => getColId(l) === cId);
          if (found) ordered.push(found);
      });
      mapped.forEach(l => {
          const lId = getColId(l);
          if (!ordered.some(o => getColId(o) === lId)) ordered.push(l);
      });

      const m = [], fs = [], fe = [];
      ordered.forEach(col => {
        if (col.title && col.title.length >= 2) {
          m.push(col);
        } else if (m.length === 0) {
          fs.push(col);
        } else {
          fe.push(col);
        }
      });

      setManageableCols(m);
      setFixedStart(fs);
      setFixedEnd(fe);
    }
  }, [open, options, columns]); 

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setManageableCols((items) => {
        const oldIndex = items.findIndex((i) => getColId(i) === active.id);
        const newIndex = items.findIndex((i) => getColId(i) === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleVisibilityChange = (uniqueId, checked) => {
    setManageableCols(prev => prev.map(col => 
      getColId(col) === uniqueId ? { ...col, visible: checked } : col
    ));
  };

  const handleWidthChange = (uniqueId, width) => {
    setManageableCols(prev => prev.map(col => 
      getColId(col) === uniqueId ? { ...col, width } : col
    ));
  };

  const handleApply = () => {
    // Pass ALL columns (visible and hidden) so the parent state keeps them all
    columnsToShowHandler([...fixedStart, ...manageableCols, ...fixedEnd]);
    setOpen(false);
  };

  const handleReset = () => {
    const resetMapped = options.map(opt => ({ ...opt, visible: true, width: undefined }));
    const m = [], fs = [], fe = [];
    resetMapped.forEach(col => {
      if (col.title && col.title.length >= 2) m.push(col);
      else if (m.length === 0) fs.push(col);
      else fe.push(col);
    });
    setManageableCols(m);
    setFixedStart(fs);
    setFixedEnd(fe);
    if (onReset) {
      onReset();
    } else {
      columnsToShowHandler([...fs, ...m, ...fe]);
    }
    setOpen(false);
  };

  const content = (
    <div className="w-[350px] flex flex-col pointer-events-auto">
      <div className="flex justify-between items-center mb-4 pb-2 border-b px-2">
        <span className="font-semibold text-gray-700 dark:text-gray-200">{t("common.manageColumns")}</span>
        <Button size="small" icon={<MdRestore />} onClick={handleReset} type="text" danger>
          {t("common.reset")}
        </Button>
      </div>
      
      <div className="overflow-y-auto pr-2 flex-1 max-h-[400px]">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext 
            items={manageableCols.map(getColId)} 
            strategy={verticalListSortingStrategy}
          >
            {manageableCols.map((col) => (
              <SortableItem 
                key={getColId(col)} 
                id={getColId(col)} 
                col={col} 
                onVisibilityChange={handleVisibilityChange}
                onWidthChange={handleWidthChange}
                t={t}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <div className="mt-4 pt-3 border-t flex justify-end gap-2 px-2">
        <Button size="small" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
        <Button size="small" type="primary" onClick={handleApply}>{t("common.apply")}</Button>
      </div>
    </div>
  );

  return (
    <Popover 
      content={content} 
      trigger="click" 
      placement="bottomRight"
      overlayClassName="manage-columns-popover"
      open={open}
      onOpenChange={setOpen}
    >
      <Button
        className="flex items-center gap-2"
        icon={<MdSettings />}
        size="large"
      >
        {t("common.columns")}
      </Button>
    </Popover>
  );
};

export default ColVisibilityDropdown;
